import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import { exportXlsx, tableRows, bold } from "../utils/xlsx";
import { escapeHtml, printHtml, printTable, tableHtml } from "../utils/printTable";

const NOTE_COLUMNS = {
  credit: [
    { key: "creditNoteNumber", label: "Credit Note #" },
    { key: "noteDate", label: "Date" },
    { key: "salesInvoiceNumber", label: "Invoice #" },
    { key: "customerName", label: "Customer" },
    { key: "reason", label: "Reason" },
    { key: "remarks", label: "Remarks" },
    { key: "totalAmount", label: "Amount", format: "money" },
  ],
  debit: [
    { key: "debitNoteNumber", label: "Debit Note #" },
    { key: "noteDate", label: "Date" },
    { key: "salesInvoiceNumber", label: "Invoice #" },
    { key: "customerName", label: "Customer" },
    { key: "reason", label: "Reason" },
    { key: "remarks", label: "Remarks" },
    { key: "totalAmount", label: "Amount", format: "money" },
  ],
};

const ITEM_COLUMNS = [
  { key: "skuCode", label: "SKU" },
  { key: "productName", label: "Product" },
  { key: "quantity", label: "Qty", format: "qty" },
  { key: "unitPrice", label: "Rate", format: "money" },
  { key: "taxAmount", label: "Tax", format: "money" },
  { key: "amount", label: "Amount", format: "money" },
];

/** One credit / debit note as a printable document (its products for a credit note). */
const printNote = (note, kind) => {
  const number = kind === "credit" ? note.creditNoteNumber : note.debitNoteNumber;
  const title = `${kind === "credit" ? "Credit" : "Debit"} Note ${number}`;
  const details = `<table><tbody>
    <tr><th style="width:18%">Note No</th><td>${escapeHtml(number)}</td><th style="width:18%">Date</th><td>${escapeHtml(note.noteDate)}</td></tr>
    <tr><th>Customer</th><td>${escapeHtml(note.customerName)}</td><th>Against Invoice</th><td>${escapeHtml(note.salesInvoiceNumber || "-")}</td></tr>
    <tr><th>Reason</th><td>${escapeHtml(note.reason)}</td><th>Amount</th><td class="r"><b>₹${escapeHtml(money(note.totalAmount))}</b></td></tr>
    <tr><th>Remarks</th><td colspan="3">${escapeHtml(note.remarks || "-")}</td></tr>
  </tbody></table>`;
  const items = note.items?.length ? `<h2>Products</h2>${tableHtml(ITEM_COLUMNS, note.items, { totals: true })}` : "";
  printHtml(title, "", `${details}${items}<p class="muted" style="margin-top:40px">Authorised Signatory: ____________________</p>`, { landscape: false });
};

const money = (value) => Number(value ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyDebitForm = { salesInvoiceId: "", reason: "PRICE_CORRECTION", totalAmount: "", noteDate: "", remarks: "", voucherTypeId: "" };

const CreditDebitNotes = () => {
  const [tab, setTab] = useState("credit");
  const [creditNotes, setCreditNotes] = useState([]);
  const [debitNotes, setDebitNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyDebitForm);
  const [saving, setSaving] = useState(false);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const [creditData, debitData] = await Promise.all([
        api.get("/api/v1/credit-notes"),
        api.get("/api/v1/debit-notes"),
      ]);
      setCreditNotes(Array.isArray(creditData) ? creditData : []);
      setDebitNotes(Array.isArray(debitData) ? debitData : []);
    } catch (error) {
      toast.error(error.message || "Failed to load credit/debit notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
    api.get("/api/v1/sales-invoices", { size: 200 }).then((data) => setInvoices(data?.content || [])).catch(() => {});
    api
      .get("/api/v1/voucher-types/by-document-type", { documentType: "DEBIT_NOTE" })
      .then((data) => setVoucherTypes(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const notesOfTab = tab === "credit" ? creditNotes : debitNotes;
  const tabTitle = tab === "credit" ? "Credit Notes" : "Debit Notes";

  const exportExcel = () => {
    const rows = notesOfTab;
    const sheets = [{ name: tabTitle, rows: [[bold(tabTitle)], [`${rows.length} note(s)`], [], ...tableRows(NOTE_COLUMNS[tab], rows, { totals: true })] }];
    if (tab === "credit") {
      // every credit note's products on a second sheet
      const itemRows = creditNotes.flatMap((note) =>
        (note.items || []).map((item) => ({ ...item, creditNoteNumber: note.creditNoteNumber, noteDate: note.noteDate, customerName: note.customerName }))
      );
      sheets.push({
        name: "Credit Note Items",
        rows: tableRows(
          [{ key: "creditNoteNumber", label: "Credit Note #" }, { key: "noteDate", label: "Date" }, { key: "customerName", label: "Customer" }, ...ITEM_COLUMNS],
          itemRows,
          { totals: true }
        ),
      });
    }
    try {
      exportXlsx(tabTitle, sheets);
    } catch (error) {
      toast.error(error.message || "Export failed");
    }
  };

  const printList = () => printTable(tabTitle, `${notesOfTab.length} note(s)`, NOTE_COLUMNS[tab], notesOfTab, { totals: true });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/api/v1/debit-notes", {
        salesInvoiceId: Number(formData.salesInvoiceId),
        reason: formData.reason,
        totalAmount: Number(formData.totalAmount),
        noteDate: formData.noteDate || undefined,
        remarks: formData.remarks || undefined,
        voucherTypeId: formData.voucherTypeId ? Number(formData.voucherTypeId) : null,
      });
      toast.success("Debit note created");
      setShowModal(false);
      setFormData(emptyDebitForm);
      loadNotes();
    } catch (error) {
      toast.error(error.message || "Failed to create debit note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Credit notes reduce what a customer owes (auto-created for sales returns and full invoice cancellations, or
        entered manually). Debit notes increase what a customer owes (price corrections, additional charges) — always
        entered manually.
      </div>

      <div className="filters-bar">
        <button className={tab === "credit" ? "primary" : "secondary"} onClick={() => setTab("credit")}>
          Credit Notes
        </button>
        <button className={tab === "debit" ? "primary" : "secondary"} onClick={() => setTab("debit")}>
          Debit Notes
        </button>
        <button className="secondary" onClick={exportExcel} disabled={notesOfTab.length === 0} style={{ marginLeft: "auto" }}>
          Export Excel
        </button>
        <button className="secondary" onClick={printList} disabled={notesOfTab.length === 0} title="Print, or save as PDF from the print dialog">
          Print / PDF
        </button>
        {tab === "debit" ? (
          <button className="primary" onClick={() => setShowModal(true)}>
            + New Debit Note
          </button>
        ) : null}
      </div>

      {tab === "credit" ? (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Credit Note #</th>
                <th>Date</th>
                <th>Invoice #</th>
                <th>Customer</th>
                <th>Reason</th>
                <th>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {creditNotes.length === 0 ? (
                <tr>
                  <td colSpan={7}>No credit notes found.</td>
                </tr>
              ) : (
                creditNotes.map((note) => (
                  <tr key={note.id}>
                    <td>{note.creditNoteNumber}</td>
                    <td>{note.noteDate}</td>
                    <td>{note.salesInvoiceNumber}</td>
                    <td>{note.customerName}</td>
                    <td>{note.reason}</td>
                    <td>{money(note.totalAmount)}</td>
                    <td>
                      <button className="secondary" onClick={() => printNote(note, "credit")} title="Print / save this credit note as PDF">
                        Print
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Debit Note #</th>
                <th>Date</th>
                <th>Invoice #</th>
                <th>Customer</th>
                <th>Reason</th>
                <th>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {debitNotes.length === 0 ? (
                <tr>
                  <td colSpan={7}>No debit notes found.</td>
                </tr>
              ) : (
                debitNotes.map((note) => (
                  <tr key={note.id}>
                    <td>{note.debitNoteNumber}</td>
                    <td>{note.noteDate}</td>
                    <td>{note.salesInvoiceNumber}</td>
                    <td>{note.customerName}</td>
                    <td>{note.reason}</td>
                    <td>{money(note.totalAmount)}</td>
                    <td>
                      <button className="secondary" onClick={() => printNote(note, "debit")} title="Print / save this debit note as PDF">
                        Print
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Create Debit Note</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid" style={{ gridTemplateColumns: "1fr" }}>
                <div className="form-group">
                  <label>Sales Invoice</label>
                  <select name="salesInvoiceId" value={formData.salesInvoiceId} onChange={handleChange} required>
                    <option value="">Select invoice</option>
                    {invoices.map((inv) => (
                      <option key={inv.id} value={inv.id}>
                        {inv.invoiceNumber}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Reason</label>
                  <select name="reason" value={formData.reason} onChange={handleChange}>
                    <option value="PRICE_CORRECTION">Price Correction</option>
                    <option value="ADDITIONAL_CHARGE">Additional Charge</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount</label>
                  <input type="number" min="0" step="0.01" name="totalAmount" value={formData.totalAmount} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Voucher Type (optional)</label>
                  <select name="voucherTypeId" value={formData.voucherTypeId} onChange={handleChange}>
                    <option value="">Default (DN numbering)</option>
                    {voucherTypes.map((vt) => (
                      <option key={vt.id} value={vt.id}>
                        {vt.name} ({vt.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" onClick={(e) => e.target.showPicker?.()} name="noteDate" value={formData.noteDate} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Remarks</label>
                  <input name="remarks" value={formData.remarks} onChange={handleChange} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CreditDebitNotes;
